/* B with U: progressive enhancement; images stay readable without animation. */
(() => {
  if (customElements.get('hut-case-motion')) return;
  class HutCaseMotion extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('enabled') || this.controller) return;
      this.controller = new AbortController();
      this.seen = this.seen || new WeakSet();
      this.effects = new Set();
      this.cursorRipples = new Map();
      this.lastRippleAt = -Infinity;
      this.timers = new Set();
      this.initFrame = requestAnimationFrame(() => this.init());
    }

    init() {
      this.initFrame = null;
      if (!this.isConnected || !this.controller) return;
      const { signal } = this.controller;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.fine = matchMedia('(hover: hover) and (pointer: fine)');
      this.glow = document.createElement('span');
      this.glow.className = 'bwu-cursor-light';
      this.glow.setAttribute('aria-hidden', 'true');
      this.append(this.glow);
      const hide = () => {
        this.pointerActive = false;
        this.glow.classList.remove('is-visible');
        this.clearCursorRipples();
      };
      const position = () => {
        this.pointerFrame = null;
        if (!this.pointerActive) return;
        const rect = this.getBoundingClientRect();
        if (this.pointerX < rect.left || this.pointerX > rect.right || this.pointerY < rect.top || this.pointerY > rect.bottom) { hide(); return; }
        this.glow.style.transform = `translate3d(${this.pointerX - rect.left - 220}px,${this.pointerY - rect.top - 220}px,0)`;
        this.glow.classList.add('is-visible');
      };
      const schedule = () => {
        if (this.pointerFrame == null) this.pointerFrame = requestAnimationFrame(position);
      };
      const movePointer = event => {
        if (event.pointerType === 'touch' || !this.fine.matches || this.reduced.matches) { hide(); return; }
        this.pointerX = event.clientX;
        this.pointerY = event.clientY;
        this.pointerActive = true;
        schedule();
        this.cursorRipple(event);
      };
      this.addEventListener('pointerenter', movePointer, { passive:true, signal });
      this.addEventListener('pointermove', movePointer, { passive:true, signal });
      this.addEventListener('pointerleave', hide, { signal });
      window.addEventListener('blur', hide, { signal });
      window.addEventListener('scroll', () => { if (this.pointerActive) schedule(); }, { capture:true, passive:true, signal });
      this.fine.addEventListener('change', hide, { signal });
      this.reduced.addEventListener('change', () => {
        hide();
        if (this.reduced.matches) this.clearEffects();
      }, { signal });

      if (!('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting || entry.intersectionRatio < .12) return;
          const img = entry.target;
          this.observer.unobserve(img);
          if (this.seen.has(img)) return;
          this.seen.add(img);
          if (!this.reduced.matches && img.naturalWidth > 0) this.reveal(img);
        });
      }, { threshold:.12 });
      this.querySelectorAll('.project-detail-media img, .bwu-process-grid img, .bwu-features img, .project-detail-figure img, .project-detail-gallery img').forEach(img => {
        const ready = () => {
          if (!signal.aborted && img.naturalWidth > 0 && !this.seen.has(img)) this.observer.observe(img);
        };
        if (img.complete) ready();
        else img.addEventListener('load', ready, { once:true, signal });
      });
    }

    cursorRipple(event) {
      const now = Date.now();
      if (now - this.lastRippleAt < 360 || this.cursorRipples.size >= 3) return;
      if (this.lastRippleAt !== -Infinity && Math.hypot(event.clientX - this.lastRippleX, event.clientY - this.lastRippleY) < 24) return;
      this.lastRippleAt = now;
      this.lastRippleX = event.clientX;
      this.lastRippleY = event.clientY;
      const rect = this.getBoundingClientRect();
      const ripple = document.createElement('span');
      ripple.className = 'bwu-cursor-ripple';
      ripple.setAttribute('aria-hidden', 'true');
      ripple.style.left = `${event.clientX - rect.left}px`;
      ripple.style.top = `${event.clientY - rect.top}px`;
      this.append(ripple);
      const timer = setTimeout(() => {
        ripple.remove();
        this.cursorRipples.delete(ripple);
      }, 1100);
      this.cursorRipples.set(ripple, timer);
    }

    clearCursorRipples() {
      this.cursorRipples.forEach((timer, ripple) => {
        clearTimeout(timer);
        ripple.remove();
      });
      this.cursorRipples.clear();
      this.lastRippleAt = -Infinity;
    }

    reveal(img) {
      const host = img.parentElement;
      const imageRect = img.getBoundingClientRect();
      if (!imageRect.width || !imageRect.height) return;
      host.classList.add('bwu-ripple-host');
      const hostRect = host.getBoundingClientRect();
      const wave = document.createElement('span');
      wave.className = 'bwu-image-ripple';
      wave.setAttribute('aria-hidden', 'true');
      wave.style.left = `${imageRect.left - hostRect.left - host.clientLeft}px`;
      wave.style.top = `${imageRect.top - hostRect.top - host.clientTop}px`;
      wave.style.width = `${imageRect.width}px`;
      wave.style.height = `${imageRect.height}px`;
      wave.style.setProperty('--bwu-wave-size', `${Math.hypot(imageRect.width, imageRect.height)}px`);
      host.append(wave);
      img.classList.add('bwu-image-arriving');
      const effect = { img, host, wave };
      this.effects.add(effect);
      const timer = setTimeout(() => {
        this.removeEffect(effect);
        this.timers.delete(timer);
      }, 1250);
      this.timers.add(timer);
    }

    removeEffect(effect) {
      effect.wave.remove();
      effect.img.classList.remove('bwu-image-arriving');
      effect.host.classList.remove('bwu-ripple-host');
      this.effects.delete(effect);
    }

    clearEffects() {
      this.effects.forEach(effect => this.removeEffect(effect));
      this.timers.forEach(clearTimeout);
      this.timers.clear();
    }

    disconnectedCallback() {
      if (!this.controller) return;
      this.controller.abort();
      this.controller = null;
      cancelAnimationFrame(this.initFrame);
      cancelAnimationFrame(this.pointerFrame);
      this.pointerFrame = null;
      this.pointerActive = false;
      this.observer?.disconnect();
      this.clearEffects();
      this.clearCursorRipples();
      this.glow?.remove();
    }
  }
  customElements.define('hut-case-motion', HutCaseMotion);
})();
